/**
 * Lista de prácticas de conversación, agrupada en "Para ti" (categorías de
 * interés) y "Más conversaciones" (el resto). Cada sección se pagina de forma
 * independiente contra el backend (scope + limit/offset) con su propio "Cargar
 * más", para que escale aunque el catálogo crezca mucho.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { useAuth } from '@/hooks/useAuth';
import { localizedField } from '@/utils/localizedField';
import { Spinner } from '@/components/atoms/Spinner';
import { Card } from '@/components/molecules/Card';
import { Button } from '@/components/atoms/Button';
import { ErrorState } from '@/components/molecules/ErrorState';
import { RevealOnScroll } from '@/components/atoms/RevealOnScroll';
import { CompletedStoryCard } from '@/pages/student/MonthlyStoriesPage/CompletedStoryCard';
import { getUserConversationAssignments } from '@/services/api/conversations.api';
import type { ConversationPracticeListItem } from '@/types/conversation.types';
import { usePageTransitionEntrance } from '@/context/PageTransitionEntranceContext';
import { entranceAwareBlockTransition } from '@/constants/storyResourcesEntrance';
import { ONBOARDING_CLOSED_EVENT } from '@/constants/events';
import { buildRoute, ROUTES } from '@/constants/routes';
import styles from './ConversationsPage.module.scss';

const EASE: [number, number, number, number] = [0.42, 0, 0.58, 1];
const PAGE_SIZE = 6;
/** Igual que MonthlyChallengesPage: fila a fila (índice 0 = primera celda, luego derecha, etc.). */
const CARD_INITIAL_BASE_DELAY = 0.5;
const CARD_INDEX_STAGGER = 0.04;
const CARD_INDEX_MAX_STAGGER = 0.22;

type ConvNavState = {
  scrollToResourceId?: string;
  scrollY?: number;
  returnFromConversation?: boolean;
  forYouLoaded?: number;
  moreLoaded?: number;
} | null;

export function ConversationsPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const pageReady = usePageTransitionEntrance();
  const reducedMotion = Boolean(useReducedMotion());

  const hidden = { opacity: 0, y: 20 };
  const visible = { opacity: 1, y: 0 };
  const fadeUp = useMemo(
    () => (delay: number) => ({
      initial: hidden,
      animate: pageReady ? visible : hidden,
      transition: entranceAwareBlockTransition(pageReady, reducedMotion, 0.42, delay),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pageReady, reducedMotion]
  );

  const navState = location.state as ConvNavState;
  const level = user?.level;

  // ¿Este montaje viene de volver de una conversación? Entonces las cards
  // aparecen sin animación (no re-animar al restaurar/subir).
  const isRestore = !!navState?.returnFromConversation;

  // Dos secciones independientes: "Para ti" (interest) y "Más" (other).
  const [forYou, setForYou] = useState<ConversationPracticeListItem[]>([]);
  const [more, setMore] = useState<ConversationPracticeListItem[]>([]);
  const [forYouOffset, setForYouOffset] = useState(0);
  const [moreOffset, setMoreOffset] = useState(0);
  const [forYouHasMore, setForYouHasMore] = useState(false);
  const [moreHasMore, setMoreHasMore] = useState(false);
  const [loadingForYouMore, setLoadingForYouMore] = useState(false);
  const [loadingMoreMore, setLoadingMoreMore] = useState(false);
  // El botón "Cargar más" de cada sección no aparece hasta que la última card de
  // esa sección ha terminado de animar (igual que en Historias): así no se ve el
  // botón con las cards aún cargándose.
  const [forYouLoadMoreVisible, setForYouLoadMoreVisible] = useState(false);
  const [moreLoadMoreVisible, setMoreLoadMoreVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  // Se incrementa para forzar recarga (cambio de nivel / cierre de onboarding).
  const [refreshNonce, setRefreshNonce] = useState(0);

  // Carga inicial (y restauración al volver de una conversación: recargamos cada
  // sección hasta lo que había cargado para poder restaurar el scroll).
  useEffect(() => {
    if (!level) {
      setIsLoading(false);
      return;
    }
    const st = navState;
    const restore = !!st?.returnFromConversation;
    const fyLimit = restore && st?.forYouLoaded ? Math.max(st.forYouLoaded, PAGE_SIZE) : PAGE_SIZE;
    const mLimit = restore && st?.moreLoaded ? Math.max(st.moreLoaded, PAGE_SIZE) : PAGE_SIZE;

    let cancelled = false;
    void (async () => {
      try {
        setIsLoading(true);
        setError(false);
        // Al volver de una conversación, los botones se muestran desde el
        // principio (cards sin animación); en carga normal van gateados hasta que
        // la última card de cada sección termine de animar.
        setForYouLoadMoreVisible(restore);
        setMoreLoadMoreVisible(restore);
        const [fy, m] = await Promise.all([
          getUserConversationAssignments({ scope: 'interest', limit: fyLimit, offset: 0 }),
          getUserConversationAssignments({ scope: 'other', limit: mLimit, offset: 0 }),
        ]);
        if (cancelled) return;
        setForYou(fy.resources);
        setForYouOffset(fy.resources.length);
        setForYouHasMore(!!fy.hasMore);
        setMore(m.resources);
        setMoreOffset(m.resources.length);
        setMoreHasMore(!!m.hasMore);
      } catch {
        if (cancelled) return;
        setForYou([]);
        setMore([]);
        setForYouHasMore(false);
        setMoreHasMore(false);
        setError(true);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Recargar al cambiar de nivel o forzar refresh. (navState se lee al montar.)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level, refreshNonce]);

  // Al cerrarse el onboarding (puede haber cambiado el nivel/intereses) recargamos.
  useEffect(() => {
    const handler = () => setRefreshNonce((n) => n + 1);
    window.addEventListener(ONBOARDING_CLOSED_EVENT, handler);
    return () => window.removeEventListener(ONBOARDING_CLOSED_EVENT, handler);
  }, []);

  const loadMoreForYou = async () => {
    if (loadingForYouMore || !forYouHasMore) return;
    setLoadingForYouMore(true);
    try {
      const res = await getUserConversationAssignments({
        scope: 'interest',
        limit: PAGE_SIZE,
        offset: forYouOffset,
      });
      setForYou((prev) => [...prev, ...res.resources]);
      setForYouOffset((prev) => prev + res.resources.length);
      setForYouHasMore(!!res.hasMore);
    } catch {
      // Silencioso: el botón sigue disponible para reintentar.
    } finally {
      setLoadingForYouMore(false);
    }
  };

  const loadMoreMore = async () => {
    if (loadingMoreMore || !moreHasMore) return;
    setLoadingMoreMore(true);
    try {
      const res = await getUserConversationAssignments({
        scope: 'other',
        limit: PAGE_SIZE,
        offset: moreOffset,
      });
      setMore((prev) => [...prev, ...res.resources]);
      setMoreOffset((prev) => prev + res.resources.length);
      setMoreHasMore(!!res.hasMore);
    } catch {
      // Silencioso.
    } finally {
      setLoadingMoreMore(false);
    }
  };

  // Restaurar posición de scroll al volver de una conversación.
  const scrollRestoredRef = useRef(false);
  useEffect(() => {
    if (!navState?.scrollToResourceId || scrollRestoredRef.current || isLoading) return;
    const timer = setTimeout(() => {
      scrollRestoredRef.current = true;
      if (navState.scrollY != null) {
        window.scrollTo(0, navState.scrollY);
      } else {
        const el = document.getElementById(`resource-card-${navState.scrollToResourceId}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      navigate(ROUTES.STUDENT.CONVERSATIONS, { replace: true, state: {} });
    }, 100);
    return () => clearTimeout(timer);
  }, [navState?.scrollToResourceId, navState?.scrollY, isLoading, navigate]);

  const handleOpen = useCallback(
    (resourceId: string) => {
      // Pasamos al detalle cuántas cards había cargadas en cada sección; al
      // cerrar, el wrapper las reenvía de vuelta (junto a returnFromConversation)
      // para restaurar las páginas cargadas y el scroll (igual que Historias).
      navigate(buildRoute(ROUTES.STUDENT.CONVERSATION, { id: resourceId }), {
        state: {
          scrollY: window.scrollY,
          forYouLoaded: forYou.length,
          moreLoaded: more.length,
        },
      });
    },
    [navigate, forYou.length, more.length]
  );

  const totalLoaded = forYou.length + more.length;
  const showInitialLoading = isLoading && totalLoaded === 0;
  const hasForYou = forYou.length > 0;

  const renderCards = (
    items: ConversationPracticeListItem[],
    indexOffset: number,
    onLastComplete?: () => void
  ) =>
    items.map((resource, i) => {
      const idx = indexOffset + i;
      const isLast = i === items.length - 1;
      const cardDelay =
        CARD_INITIAL_BASE_DELAY + Math.min(idx * CARD_INDEX_STAGGER, CARD_INDEX_MAX_STAGGER);
      return (
        <RevealOnScroll
          key={resource.id}
          id={`resource-card-${resource.id}`}
          delay={cardDelay}
          disabled={reducedMotion || isRestore}
          ready={pageReady}
          onShown={isLast ? onLastComplete : undefined}
        >
          <CompletedStoryCard
            title={localizedField(resource, 'title', i18n.language)}
            backgroundImage={resource.backgroundImage ?? null}
            onClick={() => handleOpen(resource.id)}
          />
        </RevealOnScroll>
      );
    });

  // Botón "Cargar más": solo aparece cuando la última card de la sección ha
  // terminado de animar (visible=true). Con reduced-motion se muestra directo.
  const loadMoreButton = (
    onClick: () => void,
    loading: boolean,
    visible: boolean,
    keyVal: number
  ) => (
    <motion.div
      key={keyVal}
      className={styles.conversationsPage__loadMore}
      initial={!reducedMotion ? { opacity: 0, y: 16 } : false}
      animate={!reducedMotion ? (visible ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }) : undefined}
      transition={{ duration: 0.38, ease: EASE }}
    >
      <Button variant="primary" onClick={onClick} disabled={loading}>
        {loading ? t('common.loading') : t('storiesPage.loadMore')}
      </Button>
    </motion.div>
  );

  return (
    <div className={styles.conversationsPage}>
      <motion.div className={styles.conversationsPage__header} {...fadeUp(0)}>
        <h1 className={styles.conversationsPage__title}>{t('conversationsPage.title')}</h1>
        <p className={styles.conversationsPage__subtitle}>{t('conversationsPage.subtitle')}</p>
      </motion.div>

      {!level ? (
        <Card className={styles.conversationsPage__empty}>
          <p>{t('dashboard.noLevelCta')}</p>
          <Button variant="primary" onClick={() => navigate(ROUTES.STUDENT.LEVEL_TEST)}>
            {t('dashboard.takeLevelTest')}
          </Button>
        </Card>
      ) : (
        <>
          {showInitialLoading && (
            <motion.div className={styles.conversationsPage__loading} {...fadeUp(0.12)}>
              <Spinner size="lg" aria-label={t('common.loading')} />
            </motion.div>
          )}

          {error && !isLoading && (
            <motion.div className={styles.conversationsPage__error} {...fadeUp(0.12)}>
              <ErrorState onRetry={() => setRefreshNonce((n) => n + 1)} />
            </motion.div>
          )}

          {!isLoading && !error && totalLoaded === 0 && (
            <motion.div className={styles.conversationsPage__empty} {...fadeUp(0.12)}>
              <p>{t('conversationsPage.empty')}</p>
            </motion.div>
          )}

          {totalLoaded > 0 && (
            <div className={styles.conversationsPage__categories}>
              {hasForYou && (
                <div className={styles.conversationsPage__category}>
                  <motion.h2 className={styles.conversationsPage__sectionTitle} {...fadeUp(0.24)}>
                    {t('conversationsPage.forYou')}
                  </motion.h2>
                  <div className={styles.conversationsPage__topicsGrid}>
                    {renderCards(forYou, 0, () => setForYouLoadMoreVisible(true))}
                  </div>
                  {forYouHasMore &&
                    loadMoreButton(
                      () => void loadMoreForYou(),
                      loadingForYouMore,
                      forYouLoadMoreVisible,
                      forYou.length
                    )}
                </div>
              )}

              {more.length > 0 && (
                <div className={styles.conversationsPage__category}>
                  {hasForYou && (
                    <motion.h2
                      className={styles.conversationsPage__sectionTitle}
                      initial={!reducedMotion ? { opacity: 0, y: 16 } : false}
                      whileInView={
                        pageReady && !reducedMotion ? { opacity: 1, y: 0 } : undefined
                      }
                      viewport={{ once: true, margin: '-40px' }}
                      transition={{ duration: 0.42, ease: EASE }}
                    >
                      {t('conversationsPage.more')}
                    </motion.h2>
                  )}
                  <div className={styles.conversationsPage__topicsGrid}>
                    {renderCards(more, forYou.length, () => setMoreLoadMoreVisible(true))}
                  </div>
                  {moreHasMore &&
                    loadMoreButton(
                      () => void loadMoreMore(),
                      loadingMoreMore,
                      moreLoadMoreVisible,
                      more.length
                    )}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

ConversationsPage.displayName = 'ConversationsPage';
export default ConversationsPage;
